import {
  BALANCE_NAMESPACE,
  COMMON_NAMESPACE,
  PAYMENT_AMOUNT_MODAL_NAMESPACE,
} from "constants/i18n-namespaces";
import { useCurrencyFormatter, useLabelsWithKrn } from "hooks";
import {
  Button,
  Modal as ForgeModal,
  ModalBody,
  ModalHeader,
} from "kubra-ux-forge";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getIsMobile } from "selectors/appState";
import { PayableAmountOption } from "types/payable";
import { getLabelWithKrn } from "utilities/helper";

export interface IPayableAmountOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  typeKrn: string | null;
  messageKey: string;
  options: PayableAmountOption[];
  selectedOptionKey: string | null;
  updateCallBack: (option: PayableAmountOption) => void;
}

export const PayableAmountOptionsModal = (
  props: IPayableAmountOptionsModalProps,
) => {
  const {
    isOpen,
    onClose,
    typeKrn,
    messageKey,
    options,
    selectedOptionKey,
    updateCallBack,
  } = props;

  const { t } = useTranslation(PAYMENT_AMOUNT_MODAL_NAMESPACE);
  const { t: tBalance } = useTranslation(BALANCE_NAMESPACE);
  const isMobile = useSelector(getIsMobile);
  const currencyFormatter = useCurrencyFormatter();
  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();

  const [selectedKey, setSelectedKey] = useState<string | null>(
    selectedOptionKey,
  );

  const handleSubmit = () => {
    const selectedOption = options.find(
      (option) => option.optionKey === selectedKey,
    );
    if (selectedOption) {
      updateCallBack(selectedOption);
    }
  };

  return (
    <ForgeModal
      open={isOpen}
      sharpCorners
      fullScreen={isMobile}
      fullPage={isMobile}
      onBackdropClick={onClose}
      dataCy="select-amount-modal"
    >
      <ModalHeader
        onClose={onClose}
        closeButtonLabel={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
        data-testid="select-amount-modal-header"
        fullPage={isMobile}
      >
        {tBalance("select-amount")}
      </ModalHeader>
      <ModalBody>
        <div className="payable-amount-options-modal-container">
          <div className="balance-amount-frame">
            <div className="ez-pay-balance-frame-small">
              <p className="payable-amount-options-message">
                {getLabelWithKrn(
                  labelSourceWithKrn.labelsWithKrn,
                  typeKrn,
                  messageKey,
                )}
              </p>
              <div className="payable-amount-options-list" role="radiogroup">
                {options.map((option) => {
                  const isSelected = selectedKey === option.optionKey;
                  const description = getLabelWithKrn(
                    labelSourceWithKrn.labelsWithKrn,
                    typeKrn,
                    option.labelKey,
                  );
                  const formattedAmount = currencyFormatter.format(
                    option.optionAmount,
                  );
                  return (
                    <label
                      key={option.optionKey}
                      className={
                        isSelected
                          ? "payable-amount-option payable-amount-option--selected"
                          : "payable-amount-option"
                      }
                      aria-label={`${formattedAmount} - ${description}`}
                      data-cy={`payable-amount-option-${option.optionKey}`}
                    >
                      <input
                        type="radio"
                        className="payable-amount-option-radio"
                        name="payable-amount-option"
                        checked={isSelected}
                        onChange={() => setSelectedKey(option.optionKey)}
                      />
                      <div className="payable-amount-option-content">
                        <div className="payable-amount-option-amount">
                          {formattedAmount}
                        </div>
                        <div className="payable-amount-option-description">
                          {description}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <Button
              type="submit"
              className="ez-pay-balance-done"
              title="Done button"
              data-cy="button-done"
              disabled={!selectedKey}
              onClick={handleSubmit}
            >
              {t("done", { ns: COMMON_NAMESPACE })}
            </Button>
          </div>
        </div>
      </ModalBody>
    </ForgeModal>
  );
};
